import React from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';

// constants
import { scriptTypes } from 'constants/scripts';
import { isEu9Limited } from 'constants/countryCodes';

// Utils
import { getGoogleCredentials } from 'util/credentials';
import { getMessage } from 'util/locale';
// Selectors
import { getBoundariesByFieldUuid } from 'boundaries/selectors';
import { getActiveFieldUuid, getActiveFieldId } from 'current/selectors';
import { getAccountCountry } from 'user/selectors';
import { getGeneratedZones, isMultiLayerZoneGenerationEnabled } from 'manual/selectors';

import { ImageLayer } from 'mapping/leaflet';

// Components
import BaseMap from 'mapping/BaseMap';
import GeneratedZonesLayer from 'mapping/layers/GeneratedZonesLayer';
import FieldOutlineLayer from 'mapping/layers/FieldOutlineLayer';
import ProductivityLegend from './legends/ProductivityLegend';

const baseString = 'vra-review-scenario-map-panel';

/** Component for rendering map on Review Zones screen (create manual script flow).  */
let ReviewGeneratedZonesMapContainer = ({
  accountCountry,
  boundaries,
  isMultiLayerEnabled,
  leafLetBounts,
  rowHover,
  scriptType,
  zones
}) => {
  const classString = classNames({
    [`${baseString}`]: true
  });
  const disclaimer = isEu9Limited(accountCountry) && scriptType === scriptTypes.PLANTING
    ? <span className={'vra-emea-disclaimer'}>{getMessage('disclaimer--emea-seed-density')}</span> 
    : null;
  const hideInputMapOnHover = rowHover ? ' hide-input-map-layer' : '';

  return (
    <div className={classString + hideInputMapOnHover} data-test-id='review-scenario-map'>
      {isMultiLayerEnabled && zones && <ProductivityLegend zones={zones} />}
      <BaseMap
        bounceAtZoomLimits={true}
        boundaries={boundaries}
        boundariesPadding={0.005} // % padding around the field
        className='vra-map'
        googleCredentials={getGoogleCredentials()}
        onLeafletZoomEnd={() => undefined}
        showScale={false}>

        { !isMultiLayerEnabled && leafLetBounts?.bounds 
          && <ImageLayer 
            bounds={leafLetBounts.bounds} 
            map={{}} 
            opacity={100} 
            spinner={true} 
            url={leafLetBounts.url} 
          />
        }
        
        {boundaries && <FieldOutlineLayer boundaryGeoJson={boundaries} /> }
        {zones && <GeneratedZonesLayer rowHover={rowHover} zones={zones}/>}
      </BaseMap>
      {disclaimer}
      <div className='vra-map-interaction-blocker' />
    </div>
  );
};

ReviewGeneratedZonesMapContainer.propTypes = {
  accountCountry: propTypes.object,
  boundaries: propTypes.object,
  isMultiLayerEnabled: propTypes.bool,
  leafLetBounts: propTypes.object,
  overlayData: propTypes.object,
  rowHover: propTypes.string,
  scriptType: propTypes.string,
  zones: propTypes.array.isRequired
};

ReviewGeneratedZonesMapContainer = connect(
  (state) => {
    const fieldUuid = getActiveFieldUuid(state);
    const selectedMapUuid = get(state, `manual.generateZones.selectedMapUuid`);
    const leafLetBounts = get(state, `imagery.${getActiveFieldId(state)}.scouting["${selectedMapUuid}"]`);
    
    return {
      accountCountry: getAccountCountry(state),
      boundaries: getBoundariesByFieldUuid(state, fieldUuid),
      isMultiLayerEnabled: isMultiLayerZoneGenerationEnabled(state),
      leafLetBounts,
      scriptType: state.current.scriptType,
      zones: getGeneratedZones(state)
    };
  }
)(ReviewGeneratedZonesMapContainer);

export default ReviewGeneratedZonesMapContainer;
